import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
// import Textarea from '../../components/common/Textarea';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import hostService from '../../api/services/hostService';
import { handleApiError } from '../../utils/helpers';
import FileInput from '../../components/common/Fileinput';
import TransferHostModal from '../../components/admin/TransferHostModal/TransferHostModal';
import { getImageUrl } from '../../utils/imageHelper';
// import TransferHostModal from '../../components/admin/TransferHostModal';
// import Textarea from '../../components/common/Textarea';

const MyHostsPage = () => {
  const { success, error: showError } = useToast();
  const { user } = useAuth();
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHost, setEditingHost] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hostToDelete, setHostToDelete] = useState(null);

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);

  // ✅ ADD THIS FUNCTION
  const handleTransferClick = (host) => {
    setSelectedHost(host);
    setShowTransferModal(true);
  };

  // ✅ ADD THIS FUNCTION
  const handleTransferSuccess = () => {
    setShowTransferModal(false);
    setSelectedHost(null);
    fetchHosts(); // Refresh the list
  };

  useEffect(() => {
    fetchHosts();
  }, []);

  const fetchHosts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Super Admin gets all hosts, Company Admin gets their own
      const response = isSuperAdmin
        ? await hostService.getAllHosts({ pageSize: 100 })
        : await hostService.getMyHosts({ pageSize: 100 });

      if (response.success) {
        setHosts(response.data || []);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingHost(null);
    setModalOpen(true);
  };

  const handleEdit = (host) => {
    // Super Admin can only view, not edit other users' hosts
    // if (isSuperAdmin && host.userId !== user.id) {
    //   showError('Super Admins can view all hosts but can only edit their own hosts');
    //   return;
    // }
    setEditingHost(host);
    setModalOpen(true);
  };

  const handleDeleteClick = (host) => {
    // Super Admin can only delete their own hosts
    // if (isSuperAdmin && host.userId !== user.id) {
    //   showError('Super Admins can only delete their own hosts');
    //   return;
    // }
    setHostToDelete(host);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await hostService.deleteHost(hostToDelete.id);
      if (response.success) {
        success('Host deleted successfully!');
        fetchHosts();
        setDeleteDialogOpen(false);
        setHostToDelete(null);
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  // Get image URL helper
  // const getImageUrl = (path) => {
  //   if (!path) return null;
  //   if (path.startsWith('http')) return path;
  //   const normalizedPath = path.replace(/\\/g, '/');
  //   const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
  //   return `${BASE_URL}/${normalizedPath}`;
  // };

  if (loading) {
    return (
      <DashboardLayout title={isSuperAdmin ? "All Hosts" : "My Hosts"}>
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading hosts..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isSuperAdmin ? "All Hosts" : "My Hosts"}
      subtitle={isSuperAdmin ? "View all hosts in the system" : "Manage your organizations"}
    >
      {/* Header with Add Button */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-gray-600">
            {isSuperAdmin
              ? `Total hosts: ${hosts.length}`
              : `You have ${hosts.length} host${hosts.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Add New Host
        </Button>
      </div>

      {error && <ErrorMessage message={error} onRetry={fetchHosts} className="mb-6" />}

      {hosts.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No hosts yet
            </h3>
            <p className="mt-1 text-gray-500">
              Get started by creating your first organization
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleCreate}>
                Add Host
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hosts.map((host) => {
            const isOwner = host.userId === user.id;

            return (
              <Card key={host.id} hover className="relative">
                {/* Owner badge for Super Admin view */}
                {isSuperAdmin && (
                  <div className="absolute top-4 right-4">
                    {isOwner ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Your Host
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Other User's Host
                      </span>
                    )}
                  </div>
                )}

                {isSuperAdmin && (
                  <Button
                    variant="secondary"
                    onClick={() => handleTransferClick(host)}
                    className="bg-blue-600 hover:bg-blue-700 text-white mb-4"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Transfer
                  </Button>
                )}

                <div className="text-center">
                  {host.logo ? (
                    <img
                      src={getImageUrl(host.logo)}
                      alt={host.name}
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-gray-100"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center mx-auto mb-4 border-4 border-gray-100">
                      <span className="text-3xl font-bold text-red-600">
                        {host.name.charAt(0)}
                      </span>
                    </div>
                  )}

                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {host.name}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[40px]">
                    {host.description}
                  </p>

                  <div className="text-sm text-gray-500 mb-4 space-y-1">
                    <p className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {host.email}
                    </p>
                    <p className="flex items-center justify-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {host.phone}
                    </p>
                  </div>

                  {/* Show owner info for Super Admin */}
                  {isSuperAdmin && host.user && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Owner</p>
                      <p className="text-sm font-medium text-gray-900">
                        {host.user.firstName} {host.user.lastName}
                      </p>
                      <p className="text-xs text-gray-600">{host.user.email}</p>
                    </div>
                  )}

                  {/* Contest count */}
                  {host._count && (
                    <div className="mb-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {host._count.contests} Contest{host._count.contests !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {/* Action buttons - Super Admin has full access */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(host)}
                      className="flex-1"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(host)}
                      className="flex-1"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Host Form Modal */}
      <HostFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingHost(null);
        }}
        host={editingHost}
        onSuccess={() => {
          fetchHosts();
          setModalOpen(false);
          setEditingHost(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Host"
        message={`Are you sure you want to delete "${hostToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {showTransferModal && selectedHost && (
        <TransferHostModal
          host={selectedHost}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedHost(null);
          }}
          onSuccess={handleTransferSuccess}
        />
      )}

    </DashboardLayout>
  );
};

// Host Form Modal Component
const HostFormModal = ({ isOpen, onClose, host, onSuccess }) => {
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    logo: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (host) {
      setFormData({
        name: host.name || '',
        email: host.email || '',
        phone: host.phone || '',
        description: host.description || '',
        logo: null
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: '',
        logo: null
      });
    }
  }, [host]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (file) => {
    setFormData(prev => ({ ...prev, logo: file }));
    if (errors.logo) {
      setErrors(prev => ({ ...prev, logo: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    if (!formData.description) newErrors.description = 'Description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('description', formData.description);
      if (formData.logo) {
        data.append('logo', formData.logo);
      }

      const response = host
        ? await hostService.updateHost(host.id, data)
        : await hostService.createHost(data);

      console.log('Host form response:', response);

      if (response.success) {
        success(`Host ${host ? 'updated' : 'created'} successfully!`);
        onSuccess();
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={host ? 'Edit Host' : 'Add New Host'}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="Organization Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Awesome Events GH"
          error={errors.name}
          required
        />

        <Input
          label="Email Address"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="events@awesome.com"
          error={errors.email}
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+233201234567"
          error={errors.phone}
          required
        />

        <Input
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="We organize the best events in Ghana"
          error={errors.description}
          rows={3}
          required
        />

        <FileInput
          label="Logo"
          name="logo"
          onChange={handleFileChange}
          accept="image/*"
          preview
          error={errors.logo}
        />

        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="flex-1"
          >
            {host ? 'Update' : 'Create'} Host
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MyHostsPage;